'use client'

import { toast } from '@/core/lib/toast'
import { clearClientCsrfToken } from '@/core/lib/fetcher/csrf.client'

import AppSheet, {
  type AppSheetMoment,
} from '@/features/veluvit/components/account/app-sheet'
import AuthModal, {
  type AuthMode,
} from '@/features/veluvit/components/account/auth-modal'
import { go } from '@/features/veluvit/lib/navigation'
import { api, primeCsrf } from '@/features/veluvit/services/api'
import {
  ensureAccount,
  parseBootstrap,
  refreshSession,
  safe,
} from '@/features/veluvit/services/session.service'
import {
  closeAll,
  openModal,
  openSheet,
} from '@/features/veluvit/stores/overlay.store'
import {
  roomFor,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { Bootstrap } from '@/features/veluvit/types/api.types'

/* 계정 흐름 — v2-live.js auth()/startChat()/ACT.v2Logout … */

let authNext: (() => void) | null = null

export function openAuth(mode: AuthMode = 'login', next?: () => void) {
  if (next !== undefined) authNext = next
  closeAll()
  openModal(<AuthModal mode={mode} />, {
    label: mode === 'register' ? '계정 만들기' : '로그인',
    key: 'auth',
  })
}

/** 로그인·가입 응답을 받은 뒤 — 이어서 하려던 일이 있으면 그리로 */
export async function finishAuth(incoming: Bootstrap, mode: AuthMode) {
  const data = parseBootstrap(incoming)
  useSessionStore.getState().applyBootstrap(data)
  const identity = useSessionStore.getState().identity
  await primeCsrf()
  if (useSessionStore.getState().identity !== identity) return
  const next = authNext
  authNext = null
  closeAll()
  next?.()
  toast.show(mode === 'register' ? '계정을 만들었어요' : '로그인했어요')
}

export const continueAsGuest = safe(async () => {
  await ensureAccount()
  const next = authNext
  authNext = null
  closeAll()
  next?.()
})

/** 일반 대화는 게스트도 이용한다. 새 시작은 현재 방을 바꾸지 않고 시작 선택 화면만 연다. */
export const startChat = safe(
  async (workId: string, options: { fresh?: boolean } = {}) => {
    closeAll()
    await ensureAccount()
    // 이어서 — 대화방 주소는 방 id(chat-route)
    const room = options.fresh ? undefined : roomFor(workId)
    go(room ? `/chat/${room.id}` : `/start/${workId}`)
  }
)

export const logout = safe(async () => {
  await api('/auth/logout/', {})
  clearClientCsrfToken()
  useSessionStore.setState({
    data: null,
    rooms: {},
    selected: {},
    fresh: {},
    ready: false,
    identity: useSessionStore.getState().identity + 1,
  })
  await primeCsrf()
  await refreshSession()
  closeAll()
  toast.show('로그아웃했어요')
})

/** 앱 설치 유도 — 한 세션에 한 번만 저절로 뜬다 (레퍼런스 S.appShown) */
export function openAppSheet(
  workId?: string,
  when: AppSheetMoment = 'default'
) {
  useUiStore.getState().set({ appShown: true })
  openSheet(<AppSheet workId={workId} when={when} />, {
    clear: true,
    label: '앱 설치',
  })
}
